import React, { useState } from "react";
import { api, formatApiError, formatCurrency } from "../../lib/api";
import { ALL_STATUSES } from "../ops/opsUtils";
import EntitySearchSelect from "./EntitySearchSelect";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X, Save, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-side Edit Loan modal.  Allows correcting any data-entry mistake on a
 * loan: re-link attorney, re-link firm, fix client info, change principal /
 * rate / funding date / status.  Posts to PATCH /api/operations/loans/{id}.
 */
export default function EditLoanModal({ loan, onClose, onSaved }) {
  const [f, setF] = useState({
    client_name: loan.client_name || "",
    client_email: loan.client_email || "",
    client_phone: loan.client_phone || "",
    attorney_email: loan.attorney_email || "",
    attorney_name: loan.attorney_name || "",
    attorney_firm: loan.attorney_firm || "",
    firm_id: loan.firm_id || "",
    firm_label: loan.attorney_firm || "",
    case_type: loan.case_type || "Auto",
    case_state: loan.case_state || "",
    principal: loan.principal,
    monthly_rate: loan.monthly_rate,
    funding_date: (loan.funding_date || "").slice(0, 10),
    max_tiers: loan.max_tiers || 6,
    current_status: loan.current_status,
    settled_amount: loan.settled_amount ?? "",
  });
  const [saving, setSaving] = useState(false);
  const u = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const diff = Object.entries(f).reduce((acc, [k, v]) => {
    if (k === "firm_label") return acc; // visual-only
    let cur = loan[k];
    if (k === "funding_date") cur = (cur || "").slice(0, 10);
    if (v !== cur && !(v === "" && cur == null)) acc.push(k);
    return acc;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (diff.length === 0) { onClose(); return; }
    setSaving(true);
    try {
      const payload = {};
      for (const k of diff) {
        if (k === "firm_label") continue;
        let v = f[k];
        if (k === "principal") v = Number(v);
        if (k === "monthly_rate") v = Number(v);
        if (k === "max_tiers") v = parseInt(v, 10);
        if (k === "settled_amount") v = v === "" || v == null ? null : Number(v);
        if (k === "funding_date") v = new Date(v).toISOString();
        payload[k] = v;
      }
      await api.patch(`/operations/loans/${loan.loan_id}`, payload);
      toast.success(`Loan ${loan.loan_id} updated (${diff.length} field${diff.length !== 1 ? "s" : ""})`);
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="edit-loan-modal">
      <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#F3F4F6] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">Edit loan · {loan.loan_id}</div>
            <h2 className="font-display text-xl text-[#0A2540] font-semibold">{loan.client_name}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="edit-loan-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-[#F0F6FF] border border-[#DBEAFE] text-[#1E40AF] text-xs px-3 py-2 rounded-md flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>All edits are logged to the audit trail. Use this to correct data-entry mistakes — not as a substitute for the schedule/advances/paydowns tabs.</span>
          </div>

          <Section title="Client">
            <Row>
              <Field label="Full name"><Input value={f.client_name} onChange={u("client_name")} data-testid="el-client-name" /></Field>
              <Field label="Email"><Input type="email" value={f.client_email} onChange={u("client_email")} data-testid="el-client-email" /></Field>
            </Row>
            <Row>
              <Field label="Phone"><Input value={f.client_phone} onChange={u("client_phone")} data-testid="el-client-phone" /></Field>
              <Field label="State"><Input value={f.case_state} onChange={u("case_state")} maxLength={2} data-testid="el-state" /></Field>
            </Row>
          </Section>

          <Section title="Attorney & Firm (relink)">
            <Field label="Attorney">
              <EntitySearchSelect
                kind="attorney"
                value={f.attorney_email}
                onSelect={(row) => setF((s) => ({
                  ...s,
                  attorney_email: row.email || "",
                  attorney_name: row.name || s.attorney_name,
                  attorney_firm: row.firm || s.attorney_firm,
                  firm_id: row.firm_id || s.firm_id,
                  firm_label: row.firm_id ? (row.firm || s.firm_label) : s.firm_label,
                }))}
                onCreateNew={(t) => setF((s) => ({ ...s, attorney_email: t }))}
                testidPrefix="el-attorney-search"
              />
            </Field>
            <Row>
              <Field label="Attorney name"><Input value={f.attorney_name} onChange={u("attorney_name")} data-testid="el-attorney-name" /></Field>
              <Field label="Attorney firm (display)"><Input value={f.attorney_firm} onChange={u("attorney_firm")} data-testid="el-attorney-firm" /></Field>
            </Row>
            <Field label="Portal firm (relink)">
              <EntitySearchSelect
                kind="firm"
                value={f.firm_label || f.attorney_firm}
                onSelect={(row) => setF((s) => ({ ...s, firm_id: row.firm_id, firm_label: row.name }))}
                onCreateNew={() => {}}
                testidPrefix="el-firm-search"
              />
              <div className="text-[11px] text-[#6B7280] mt-1">{f.firm_id ? <span className="text-[#047857]">Linked to firm portal ✓ {f.firm_id}</span> : <span>Not linked to a firm portal — attorney portal access will be denied.</span>}</div>
            </Field>
          </Section>

          <Section title="Loan terms (audited)">
            <Row>
              <Field label="Principal">
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span><Input type="number" min="100" step="100" value={f.principal} onChange={u("principal")} className="pl-7" data-testid="el-principal" /></div>
              </Field>
              <Field label="Monthly rate">
                <Input type="number" min="0.01" max="0.10" step="0.001" value={f.monthly_rate} onChange={u("monthly_rate")} data-testid="el-rate" />
                <div className="text-[11px] text-[#6B7280] mt-1">{(Number(f.monthly_rate) * 100).toFixed(2)}% per month</div>
              </Field>
            </Row>
            <Row>
              <Field label="Funding date"><Input type="date" value={f.funding_date} onChange={u("funding_date")} data-testid="el-funding-date" /></Field>
              <Field label="Max tiers"><Input type="number" min="1" max="24" value={f.max_tiers} onChange={u("max_tiers")} data-testid="el-max-tiers" /></Field>
            </Row>
            <Row>
              <Field label="Case type">
                <Select value={f.case_type} onValueChange={u("case_type")}>
                  <SelectTrigger data-testid="el-case-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Auto", "Slip & Fall", "Premises Liability", "Workplace Injury", "Wrongful Death", "Medical Malpractice", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Current status">
                <Select value={f.current_status} onValueChange={u("current_status")}>
                  <SelectTrigger data-testid="el-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </Row>
            <Row>
              <Field label="Settled amount (if applicable)">
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span><Input type="number" min="0" step="50" value={f.settled_amount} onChange={u("settled_amount")} className="pl-7" data-testid="el-settled-amount" placeholder="Leave blank to clear" /></div>
              </Field>
              <Field label=""><div /></Field>
            </Row>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#F3F4F6] px-6 py-3 flex items-center justify-between gap-2">
          <div className="text-xs text-[#6B7280]">
            {diff.length === 0 ? "No changes yet." : <><span className="font-medium text-[#0A2540]">{diff.length}</span> field{diff.length !== 1 ? "s" : ""} changed</>}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="el-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || diff.length === 0} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="el-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Save changes
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-xs uppercase tracking-wider text-[#6B7280] font-medium mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
